/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file featureFlags.js
 * @description
 * **Build-time feature switchboard** — the single place that decides which
 * login methods, app modes, modules, screens and functions a given release of
 * this app is allowed to show.
 *
 * Why it exists: an App Store review was rejected because the binary advertised
 * modules that were not fully finished, and Apple does not accept the social
 * sign-in setup we ship. Instead of forking the code, one release build turns
 * the unfinished surface OFF, so a reviewer only ever sees working screens —
 * while the normal release keeps everything.
 *
 * ── How to pick a profile ────────────────────────────────────────────────────
 * The profile is resolved from a **statically written** env var so Expo/Metro
 * can inline it at build time:
 *
 *     EXPO_PUBLIC_HAPPY_FEATURE_PROFILE=apple   npx expo run:ios
 *
 * or in `eas.json`:
 *
 *     { "build": { "apple-review": { "env": {
 *         "EXPO_PUBLIC_HAPPY_FEATURE_PROFILE": "apple" } } } }
 *
 * With nothing set the build uses {@link DEFAULT_PROFILE} (`full`).
 *
 * Two more static env vars trim a build without editing this file:
 *
 *     EXPO_PUBLIC_HAPPY_FEATURES_DISABLED="module.crm,function.voip"
 *     EXPO_PUBLIC_HAPPY_FEATURES_ENABLED="auth.social.apple"
 *
 * NOTE: only *statically written* `process.env.EXPO_PUBLIC_*` reads are inlined
 * by Metro — that is why this file never indexes `process.env` dynamically.
 *
 * ── Key vocabulary ───────────────────────────────────────────────────────────
 *   auth.<surface>          login methods (`auth.social`, `auth.social.google`, …)
 *   mode.<appMode>          restaurant | sales | manager | service | stock
 *   module.<accessKey>      a `managementAccess` rule key — the SAME key the
 *                           Modules hub, the bottom tabs and every management
 *                           screen already gate themselves with, so gating one
 *                           key hides the tile, the tab and the route at once
 *   moduleNav.<group>       a {@link module:config/moduleNavs} bottom-bar group
 *   screen.<RouteName>      any navigator route name
 *   function.<name>         a cross-cutting in-screen function
 *
 * **Unknown keys are ENABLED.** A key absent from {@link FEATURE_DEFAULTS} and
 * from the active profile resolves to `true`, so adding a new screen never
 * needs an entry here — you only list what you want to be able to switch OFF.
 *
 * A disabled parent disables its children: `auth.social=false` also switches off
 * every `auth.social.*`. Keys in {@link CORE_FEATURES} can never be disabled, so
 * no profile can lock a user out of signing in or out of their own settings.
 *
 * Dependency-free on purpose (no `react-native` / native module imports) so it
 * can also be read from `app.config.js` in plain Node if a build ever needs to.
 */

// ── Env plumbing (static reads only — see the note above) ────────────────────

/** @type {string|undefined} */
const ENV_PROFILE = process.env.EXPO_PUBLIC_HAPPY_FEATURE_PROFILE || process.env.HAPPY_FEATURE_PROFILE;
/** @type {string|undefined} */
const ENV_DISABLED = process.env.EXPO_PUBLIC_HAPPY_FEATURES_DISABLED || process.env.HAPPY_FEATURES_DISABLED;
/** @type {string|undefined} */
const ENV_ENABLED = process.env.EXPO_PUBLIC_HAPPY_FEATURES_ENABLED || process.env.HAPPY_FEATURES_ENABLED;

/**
 * Split a comma/semicolon/space separated env list into feature keys.
 * @param {string|undefined} value Raw env value.
 * @returns {string[]} Parsed keys (never null).
 */
const parseList = (value) => String(value || '')
    .split(/[,;\s]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);

// ── Defaults ─────────────────────────────────────────────────────────────────

/**
 * Every switchable feature this app ships, with the value used when the active
 * profile says nothing about it. The design baseline is `true` — profiles are
 * what turn things off — but some surfaces are currently shipped `false` here
 * (a trimmed production baseline). Flip a default back to `true` to restore it
 * everywhere, or re-enable per build via `EXPO_PUBLIC_HAPPY_FEATURES_ENABLED`
 * without a code change.
 *
 * Keys are grouped exactly the way the app is: login → modes → modules →
 * module bottom-bars → screens → functions.
 * @type {Object.<string, boolean>}
 */
export const FEATURE_DEFAULTS = {
    // ── Login methods ────────────────────────────────────────────────────────
    'auth.local': true,             // e-mail + password (core, never off)
    // Re-enabled 2026-09-13 after the App Store review that forced the temporary
    // hide (2026-08-24). Normal releases show the provider row again; the
    // `apple` review profile still switches `auth.social` off on its own, so a
    // review build is unaffected by these defaults.
    'auth.social': true,            // master switch for every social provider
    'auth.social.google': true,
    'auth.social.apple': true,
    'auth.social.facebook': true,
    'auth.social.microsoft': true,
    'auth.social.github': true,
    'auth.social.linkedin': true,
    'auth.biometric': false,         // Face ID / Touch ID quick unlock
    'auth.registration': false,      // self-service sign-up
    // Forgotten password was shipped hidden (false), which left users with no
    // way to recover an account from the app. On by default since 2026-10-14.
    'auth.passwordReset': true,      // forgotten password (OTP by e-mail / SMS)
    'auth.magicLink': true,          // passwordless sign-in: e-mailed link + 6-digit code
    'auth.guestChat': false,         // pre-login AI chat

    // ── Operational app modes (appVariantManifest AppMode) ───────────────────
    'mode.restaurant': true,        // POS till
    'mode.sales': true,             // Sales (CRM field sales)
    'mode.manager': true,           // Management
    'mode.service': true,           // Service
    'mode.stock': true,             // Warehouse
    'mode.driver': true,            // Driver / courier (Sofor app)

    // ── Modules (managementAccess keys — hub tile + tab + route in one) ──────
    'module.dashboard': true,
    'module.data': true,            // the Modules hub itself
    'module.settings': true,        // (core, never off)
    'module.orders': true,
    // Products mode shipped admin-grade (server search+pagination, category
    // filter/select, barcode assignment, archive/restore) — flipped back on.
    'module.products': true,
    'module.customers': true,
    'module.crm': true,
    // Apollo.io prospecting (lead search + enrichment). A PAID external data
    // provider billed per revealed contact, so it is switchable on its own and
    // not folded into `module.crm`: a store may license the CRM and still not
    // want reps spending Apollo credits.
    'module.apollo': true,
    'module.service': true,
    // In-app Time Tracker (projects + work orders) and the home-screen widget's
    // "+ New task" entry point into it.
    'module.timeTracking': true,
    // AI meeting recorder + transcript detail (screens/meetings/*).
    'module.meetings': true,
    'module.finance': true,
    'module.stock': true,
    // Procurement (Beszerzés) — the full PR → RFQ → PO → receipt → 3-way-match
    // cycle. Its own key rather than part of `module.stock`/`module.logistics`:
    // the backend licences it separately ('Procurement module'), so a warehouse
    // store without it must not see the tile.
    'module.procurement': true,
    // The AI sourcing planner inside it. Switchable on its own because an
    // analysis WITH its narrative spends the store's AI credits — the same
    // reasoning `module.apollo` and `function.aiAssistant` carry.
    'module.procurementSourcing': true,
    // Driver / courier (Sofor) app: van stock, route execution, field sales.
    'module.fleet': true,           // Flotta (fleet fuel / refuel / break statistics)
    'module.utilities': true,       // Rezsi nyilvántartás (utility bills & meters)
    'module.driver': true,
    'module.logistics': true,
    'module.integrations': false,
    'module.automations': false,
    'module.store': true,
    'module.users': true,
    'module.roles': true,
    'module.moduleTemplates': false,
    'module.billing': true,
    'module.deployments': false,
    'module.systemStatus': false,

    // ── Module bottom-bar groups (config/moduleNavs.js) ──────────────────────
    'moduleNav.sales': true,
    'moduleNav.salesModule': true,  // field-sales module bar (app mode `sales`)
    'moduleNav.finance': true,
    'moduleNav.service': true,
    'moduleNav.crm': true,
    'moduleNav.procurement': true,
    'moduleNav.logistics': true,
    'moduleNav.team': true,
    'moduleNav.system': false,
    'moduleNav.mail': true,

    // ── Cross-cutting functions ──────────────────────────────────────────────
    'function.mail': true,          // in-app mail client
    'function.calendar': false,
    'function.aiAssistant': true,   // "ask anything" AI assistant chat (default ON; see isAiAssistantEnabled)
    'function.meetingTranscript': false,
    'function.voip': false,          // in-app calling
    'function.sms': true,
    'function.whatsapp': false,
    'function.supportChat': false,
    'function.teamChat': true,       // internal Team Chat + video meetings (store module `csapatchat`)
    'function.demoMode': true,      // "Bemutató" presentation mode
    'function.tutorial': true,      // guided tour overlay
    'function.printing': true,
    'function.payments': true,      // card payment terminals
    'function.haccp': true,
};

/**
 * Features that can never be switched off — disabling them would leave a user
 * unable to sign in or unable to reach their own settings. A profile listing
 * one of these is ignored for that key.
 * @type {string[]}
 */
export const CORE_FEATURES = [
    'auth.local',
    'module.dashboard',
    'module.settings',
];

// ── Release profiles ─────────────────────────────────────────────────────────

/**
 * @typedef {object} ReleaseProfile
 * @property {string} label        Human description (shown in dev logs).
 * @property {string[]} [disable]  Keys forced OFF for this release.
 * @property {string[]} [enable]   Keys forced back ON (applied after `disable`).
 * @property {boolean} [coreOnly]  Start from "everything off but core", then apply `enable`.
 */

/**
 * The release presets.
 *
 * `full` is the normal store/internal release: the whole app.
 *
 * `apple` is the App Store review build — social sign-in off (Apple rejects our
 * provider set) plus the surfaces that are not finished enough to be reviewed.
 *
 * **The `apple` disable list is the one place to edit** as screens land: add a
 * key to hide it from the review build, remove it once the screen is complete.
 * Everything not listed stays visible, so a `full`-quality module never
 * disappears by accident.
 *
 * @type {Object.<string, ReleaseProfile>}
 */
export const RELEASE_PROFILES = {
    full: {
        label: 'Full release — every module and login method',
    },

    apple: {
        label: 'App Store review build — finished screens only, no social sign-in',
        disable: [
            // Apple does not accept our third-party sign-in setup: e-mail +
            // password (and biometric unlock of a saved session) only.
            'auth.social',
            'auth.registration',

            // Operator/dev tooling that has no place in a reviewed consumer
            // build — a reviewer reaching these sees infrastructure, not product.
            'module.deployments',
            'module.systemStatus',
            'module.moduleTemplates',
            'module.integrations',
            'module.roles',

            // Apollo.io is an EXTERNAL, PAID third-party data provider: every
            // reveal spends real credits against the store's balance and the
            // results are personal contact data sourced outside this app. A
            // reviewer must not be able to trigger a billable third-party call,
            // and the screen would answer with a credit/consent wall anyway —
            // which reads as a broken feature. Stays ON in `full`, where the
            // store deliberately licensed it.
            'module.apollo',

            // Same reasoning one module deeper: the procurement SOURCING planner
            // spends the store's AI credits per run, so a reviewer must not be
            // able to trigger a billable call. The rest of the procurement module
            // (requisitions / RFQs / orders / settings) stays ON — it is finished
            // and spends nothing.
            'module.procurementSourcing',

            // ── Tune below as screens land ───────────────────────────────────
            // Uncomment a line to hide that surface from the review build.
            // 'module.crm',
            // 'module.finance',
            // 'module.logistics',
            // 'module.automations',
            // 'module.billing',
            // 'mode.stock',
            // 'mode.service',
            // 'function.voip',
            // 'function.whatsapp',
            // 'function.meetingTranscript',
            // 'function.demoMode',

            // The AI assistant spends real per-message AI credits against the
            // store's balance (same reasoning as Apollo above): a reviewer must
            // not be able to trigger billable AI turns, and an empty-credit
            // store answers with a paywall that reads as a broken feature.
            'function.aiAssistant',
        ],
    },

    minimal: {
        label: 'Core only — opt features back in explicitly',
        coreOnly: true,
        enable: [
            'mode.restaurant',
            'module.orders',
            'module.products',
        ],
    },
};

/** Profile used when nothing is configured. */
export const DEFAULT_PROFILE = 'full';

/**
 * The profile this binary was built with.
 * @returns {string} A key of {@link RELEASE_PROFILES}.
 */
export const getFeatureProfileName = () => {
    const requested = String(ENV_PROFILE || '').trim().toLowerCase();

    return requested && RELEASE_PROFILES[requested] ? requested : DEFAULT_PROFILE;
};

// ── Resolution ───────────────────────────────────────────────────────────────

/**
 * Every ancestor of a dotted key, nearest first (`a.b.c` → `['a.b', 'a']`).
 * @param {string} key Feature key.
 * @returns {string[]} Ancestor keys.
 */
const ancestorsOf = (key) => {
    const parts = String(key || '').split('.');
    const out = [];

    for (let i = parts.length - 1; i > 0; i -= 1) {
        out.push(parts.slice(0, i).join('.'));
    }

    return out;
};

/**
 * Resolve the final flag map once, at module load. Order (later wins):
 * defaults → profile → `EXPO_PUBLIC_HAPPY_FEATURES_DISABLED`/`_ENABLED` →
 * parent cascade → core protection.
 * @returns {{profile: string, flags: Object.<string, boolean>}} Resolved state.
 */
const resolveFlags = () => {
    const profileName = getFeatureProfileName();
    const profile = RELEASE_PROFILES[profileName] || RELEASE_PROFILES[DEFAULT_PROFILE];
    const core = new Set(CORE_FEATURES);

    /** @type {Object.<string, boolean>} */
    const flags = {...FEATURE_DEFAULTS};

    if (profile.coreOnly) {
        Object.keys(flags).forEach((key) => {
            flags[key] = core.has(key);
        });
    }
    (profile.disable || []).forEach((key) => {
        flags[key] = false;
    });
    (profile.enable || []).forEach((key) => {
        flags[key] = true;
    });

    parseList(ENV_DISABLED).forEach((key) => {
        flags[key] = false;
    });
    parseList(ENV_ENABLED).forEach((key) => {
        flags[key] = true;
    });

    // A disabled parent disables every descendant (`auth.social` → `auth.social.*`).
    // Iterated so a chain settles; the map is small, so this is cheap.
    for (let pass = 0; pass < 4; pass += 1) {
        let changed = false;

        Object.keys(flags).forEach((key) => {
            if (flags[key] && ancestorsOf(key).some((parent) => flags[parent] === false)) {
                flags[key] = false;
                changed = true;
            }
        });

        if (!changed) {
            break;
        }
    }

    // Core features survive every profile and every env override.
    core.forEach((key) => {
        flags[key] = true;
    });

    return {profile: profileName, flags};
};

const RESOLVED = resolveFlags();

/**
 * The resolved flag map for this build. Read-only — treat it as frozen data.
 * @type {Object.<string, boolean>}
 */
export const FEATURES = RESOLVED.flags;

/** @returns {string} The active release profile name. */
export const getActiveProfile = () => RESOLVED.profile;

/** @returns {boolean} Whether this binary was built for App Store review. */
export const isReviewBuild = () => RESOLVED.profile === 'apple';

/**
 * Whether a feature is available in this build.
 *
 * Unknown keys resolve through their nearest known ancestor and otherwise
 * **fail open** (enabled), so a screen that nobody registered here is never
 * hidden by accident.
 *
 * @param {string} key Feature key, e.g. `auth.social.google` or `module.crm`.
 * @returns {boolean} True when the feature may be shown.
 */
export const isFeatureEnabled = (key) => {
    if (!key) {
        return true;
    }
    if (Object.prototype.hasOwnProperty.call(FEATURES, key)) {
        return FEATURES[key] !== false;
    }

    const ancestor = ancestorsOf(key).find((parent) => Object.prototype.hasOwnProperty.call(FEATURES, parent));

    return ancestor ? FEATURES[ancestor] !== false : true;
};

/**
 * Whether at least one of the keys is enabled (e.g. "is ANY social provider on").
 * @param {string[]} keys Feature keys.
 * @returns {boolean} True when any key is enabled.
 */
export const isAnyFeatureEnabled = (keys = []) => (Array.isArray(keys) ? keys : []).some(isFeatureEnabled);

/**
 * Filter a list down to the entries whose feature is enabled.
 * @template T
 * @param {T[]} items The list to filter.
 * @param {(item: T) => string} keyOf Maps an item to its feature key.
 * @returns {T[]} Only the entries that survive the switchboard.
 */
export const filterEnabled = (items, keyOf) => (Array.isArray(items) ? items : [])
    .filter((item) => isFeatureEnabled(keyOf(item)));

/**
 * Convenience for a `managementAccess` rule key — the hub tiles, bottom tabs and
 * management screens all gate on the same key vocabulary.
 * @param {string} accessKey managementAccess rule key (e.g. `crm`, `finance`).
 * @returns {boolean} True when the module may be shown.
 */
export const isModuleEnabled = (accessKey) => isFeatureEnabled(`module.${accessKey}`);

/**
 * Convenience for a navigator route name.
 * @param {string} routeName React Navigation route name.
 * @returns {boolean} True when the screen may be reached.
 */
export const isScreenEnabled = (routeName) => isFeatureEnabled(`screen.${routeName}`);

/**
 * Feature key of the "ask anything" AI assistant chat — shared by every entry
 * point (mode menus, dashboard FAB, navigator) so the vocabulary stays single.
 * @type {string}
 */
export const AI_ASSISTANT_FEATURE_KEY = 'function.aiAssistant';

/**
 * Whether the "ask anything" AI assistant is available for a store.
 *
 * Default ON: the build-time `function.aiAssistant` flag ships enabled and a
 * store only loses the assistant by explicitly disabling it — an
 * `aiAssistantEnabled: false` on the restaurant (or its `settings`), the
 * store-level kill switch admin can stamp onto the document. This mirrors the
 * store-module gate's default-open semantics (`enabledModuleIds == null` ⇒
 * everything visible, see `managementAccess.js`): the admin Module Templates
 * catalog does not govern an AI module id yet, so an allow-list check would
 * silently hide the assistant for every store that customized its modules
 * before the id exists. Once `governableModules` ships an `ai-asszisztens`
 * id, extend this helper with the allow-list clause.
 *
 * @param {object} [restaurant] The active restaurant document (from scope/AsyncStorage).
 * @returns {boolean} True when the assistant entry points may be shown.
 */
export const isAiAssistantEnabled = (restaurant) => {
    if (!isFeatureEnabled(AI_ASSISTANT_FEATURE_KEY)) {
        return false;
    }
    if (restaurant?.aiAssistantEnabled === false || restaurant?.settings?.aiAssistantEnabled === false) {
        return false;
    }

    return true;
};

/**
 * Every key this build switched OFF — handy for a debug/settings readout.
 * @returns {string[]} Disabled keys, sorted.
 */
export const getDisabledFeatures = () => Object.keys(FEATURES)
    .filter((key) => FEATURES[key] === false)
    .sort();

export default FEATURES;
